import { useEffect, useRef, useState, type ReactNode } from 'react';

interface LazySectionProps {
  children: ReactNode;
  className?: string;
  threshold?: number;
  rootMargin?: string;
  minHeight?: number;
  placeholderBg?: string;
  /** @deprecated label is no longer used — aria-label on a plain div is invalid */
  label?: string;
}

function LazySection({
  children,
  className = '',
  threshold = 0.01,
  rootMargin = '100px',
  minHeight = 500,
  placeholderBg = '#ffffff',
}: LazySectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return (
    <div
      ref={sectionRef}
      className={className}
      style={{
        minHeight: isVisible ? undefined : minHeight,
        backgroundColor: isVisible ? undefined : placeholderBg,
        contentVisibility: isVisible ? 'visible' : 'auto',
        containIntrinsicSize: `0 ${minHeight}px`,
      }}
    >
      {isVisible ? children : null}
    </div>
  );
}

// Named export so pages can use: import { LazySection } from '...'
export { LazySection };

// Default export for backward compatibility
export default LazySection;
